// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";

export interface TimeSlot {
  id: string;
  tournament_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

export interface VenueCourt {
  id: string;
  event_id: string;
  name: string;
  notes?: string | null;
  sort_order?: number;
  is_active: boolean;
}

export interface TournamentReferee {
  id: string;
  tournament_id: string;
  name: string;
  email?: string;
  phone?: string;
  certification_level?: string;
  availability?: any;
}

export interface Match {
  id: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  pool_id?: string;
  team1_id?: string;
  team2_id?: string;
  team1_name?: string;
  team2_name?: string;
  status: string;
  scheduled_time?: string;
  venue_id?: string;
  court_field?: string;
}

export interface SchedulingConflict {
  type: 'team_conflict' | 'court_conflict' | 'referee_conflict';
  message: string;
  matchId: string;
}

export class TournamentScheduler {
  private tournamentId: string;
  private matches: Match[] = [];
  private timeSlots: TimeSlot[] = [];
  private courts: VenueCourt[] = [];
  private referees: TournamentReferee[] = [];
  private venueId: string | null = null;
  private eventId: string | null = null;

  constructor(tournamentId: string) {
    this.tournamentId = tournamentId;
  }

  async loadData() {
    // Load tournament's venue first
    await this.loadTournamentVenue();
    
    await Promise.all([
      this.loadMatches(),
      this.loadTimeSlots(),
      this.loadCourts(),
      this.loadReferees()
    ]);
  }

  private async loadTournamentVenue() {
    // Get tournament's event to find the venue
    const { data: tournament } = await supabase
      .from('tournaments')
      .select('event_id')
      .eq('id', this.tournamentId)
      .single();
    
    if (tournament?.event_id) {
      this.eventId = tournament.event_id;
      const { data: event } = await supabase
        .from('events')
        .select('venue_id')
        .eq('id', tournament.event_id)
        .single();
      
      this.venueId = event?.venue_id || null;
      console.log(`[Scheduler] Tournament venue_id: ${this.venueId}`);
    }
  }

  private async loadMatches() {
    const { data } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', this.tournamentId)
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });
    
    this.matches = data || [];
  }

  private async loadTimeSlots() {
    const { data } = await supabase
      .from('tournament_time_slots')
      .select('*')
      .eq('tournament_id', this.tournamentId)
      .order('start_time', { ascending: true });
    
    this.timeSlots = data || [];
  }

  private async loadCourts() {
    // Courts are scoped to the event being scheduled.
    if (this.eventId) {
      const { data } = await supabase
        .from('event_courts')
        .select('*')
        .eq('event_id', this.eventId)
        .eq('is_active', true)
        .order('sort_order');
      
      this.courts = data || [];
      console.log(`[Scheduler] Loaded ${this.courts.length} courts for event ${this.eventId}`);
    } else {
      this.courts = [];
      console.warn('[Scheduler] No event found, no courts loaded');
    }
  }

  private async loadReferees() {
    const { data } = await supabase
      .from('tournament_referees')
      .select('*')
      .eq('tournament_id', this.tournamentId);
    
    this.referees = data || [];
  }

  async autoSchedule(): Promise<SchedulingConflict[]> {
    const conflicts: SchedulingConflict[] = [];
    
    // Filter to only schedule matches where BOTH teams are known and not already scheduled
    const schedulableMatches = this.matches.filter(match => 
      match.team1_id !== null && 
      match.team2_id !== null &&
      !match.scheduled_time // Not already scheduled
    );
    
    const tbdMatches = this.matches.filter(match =>
      match.team1_id === null || match.team2_id === null  
    );
    
    console.log(`[Scheduler] Total matches: ${this.matches.length}`);
    console.log(`[Scheduler] Schedulable matches (teams known, unscheduled): ${schedulableMatches.length}`);
    console.log(`[Scheduler] TBD matches (skipping): ${tbdMatches.length}`);
    console.log(`[Scheduler] Available time slots: ${this.timeSlots.length}`);
    console.log(`[Scheduler] Available courts: ${this.courts.length}`);
    console.log(`[Scheduler] Court names: ${this.courts.map(c => c.name).join(', ')}`);
    
    if (schedulableMatches.length === 0) {
      console.log('[Scheduler] No matches with known teams to schedule');
      return conflicts;
    }

    if (this.timeSlots.length === 0) {
      conflicts.push({
        type: 'court_conflict',
        message: 'No time slots available. Create time slots first.',
        matchId: schedulableMatches[0]?.id || ''
      });
      return conflicts;
    }

    if (this.courts.length === 0) {
      conflicts.push({
        type: 'court_conflict',
        message: 'No courts available for this venue. Add courts to the venue first.',
        matchId: schedulableMatches[0]?.id || ''
      });
      return conflicts;
    }

    // Track what's scheduled in each time slot
    // Map: timeSlotId -> { courtId -> matchId, teams -> Set of team IDs playing in this slot }
    const slotAssignments = new Map<string, { 
      courts: Map<string, string>, 
      teams: Set<string> 
    }>();

    // Initialize all time slots
    for (const slot of this.timeSlots) {
      slotAssignments.set(slot.id, { 
        courts: new Map(), 
        teams: new Set() 
      });
    }

    // Track scheduled matches for database update
    const scheduledMatches = new Map<string, { timeSlot: string; court: VenueCourt }>();
    
    // Sort matches: pool play first, then by round number
    const sortedMatches = [...schedulableMatches].sort((a, b) => {
      // Pool matches before playoff matches
      if (a.bracket_type === 'pool' && b.bracket_type !== 'pool') return -1;
      if (a.bracket_type !== 'pool' && b.bracket_type === 'pool') return 1;
      // Then by round number
      if (a.round_number !== b.round_number) return a.round_number - b.round_number;
      // Then by match number
      return a.match_number - b.match_number;
    });

    console.log(`[Scheduler] Processing ${sortedMatches.length} matches...`);

    // Try to schedule each match
    for (const match of sortedMatches) {
      let scheduled = false;
      
      // Try each time slot in order
      for (const timeSlot of this.timeSlots) {
        const slotData = slotAssignments.get(timeSlot.id)!;
        
        // Check if teams in this match are already playing in this time slot
        const matchTeams = [match.team1_id, match.team2_id].filter(Boolean) as string[];
        const hasTeamConflict = matchTeams.some(teamId => slotData.teams.has(teamId));
        
        if (hasTeamConflict) {
          // Can't schedule here - team already playing
          continue;
        }
        
        // Find an available court in this slot (by court ID, not name)
        let availableCourt: VenueCourt | undefined;
        for (const court of this.courts) {
          if (!slotData.courts.has(court.id)) {
            availableCourt = court;
            break;
          }
        }
        
        if (!availableCourt) {
          // All courts used in this slot, try next slot
          continue;
        }
        
        // Schedule the match!
        slotData.courts.set(availableCourt.id, match.id);
        matchTeams.forEach(teamId => slotData.teams.add(teamId));
        
        scheduledMatches.set(match.id, {
          timeSlot: timeSlot.id,
          court: availableCourt
        });
        
        scheduled = true;
        console.log(`[Scheduler] Scheduled match ${match.match_number} at ${timeSlot.start_time} on ${availableCourt.name}`);
        break; // Move to next match
      }
      
      if (!scheduled) {
        conflicts.push({
          type: 'court_conflict',
          message: `Could not find available slot for match ${match.match_number} (all slots full or team conflicts)`,
          matchId: match.id
        });
      }
    }

    // Log slot usage summary
    let totalScheduledInSlots = 0;
    for (const [slotId, slotData] of slotAssignments) {
      const matchCount = slotData.courts.size;
      if (matchCount > 0) {
        const slot = this.timeSlots.find(s => s.id === slotId);
        console.log(`[Scheduler] Slot ${slot?.start_time}: ${matchCount} matches`);
        totalScheduledInSlots += matchCount;
      }
    }

    // Apply the scheduling to database
    await this.applyScheduling(scheduledMatches);
    
    console.log(`[Scheduler] Successfully scheduled ${scheduledMatches.size} matches`);
    console.log(`[Scheduler] Conflicts: ${conflicts.length}`);
    
    return conflicts;
  }

  private async applyScheduling(
    scheduledMatches: Map<string, { timeSlot: string; court: VenueCourt }>
  ) {
    const updates: any[] = [];
    
    for (const [matchId, schedule] of scheduledMatches) {
      const timeSlot = this.timeSlots.find(ts => ts.id === schedule.timeSlot);
      
      if (timeSlot && schedule.court) {
        updates.push({
          id: matchId,
          scheduled_time: timeSlot.start_time,
          venue_id: this.venueId,
          court_field: schedule.court.name
        });
      }
    }
    
    console.log(`[Scheduler] Applying ${updates.length} schedule updates to database`);
    
    // Batch update matches
    for (const update of updates) {
      const { error } = await supabase
        .from('tournament_matches')
        .update({
          scheduled_time: update.scheduled_time,
          venue_id: update.venue_id,
          court_field: update.court_field
        })
        .eq('id', update.id);
      
      if (error) {
        console.error(`[Scheduler] Error updating match ${update.id}:`, error);
      }
    }
  }

  // Method to clear existing schedule (useful for re-scheduling)
  async clearSchedule(): Promise<void> {
    const { error } = await supabase
      .from('tournament_matches')
      .update({
        scheduled_time: null,
        venue_id: null,
        court_field: null
      })
      .eq('tournament_id', this.tournamentId);
    
    if (error) {
      console.error('[Scheduler] Error clearing schedule:', error);
      throw error;
    }
    
    console.log('[Scheduler] Schedule cleared');
    await this.loadMatches();
  }

  async createTimeSlots(
    startDate: Date,
    endDate: Date,
    startTime: string,
    endTime: string,
    matchDurationMinutes: number,
    breakMinutes: number = 15
  ) {
    const timeSlots: Omit<TimeSlot, 'id'>[] = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const dayStart = new Date(current);
      const [startHour, startMinute] = startTime.split(':').map(Number);
      dayStart.setHours(startHour, startMinute, 0, 0);
      
      const dayEnd = new Date(current);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      dayEnd.setHours(endHour, endMinute, 0, 0);
      
      let slotStart = new Date(dayStart);
      
      while (slotStart < dayEnd) {
        const slotEnd = new Date(slotStart.getTime() + (matchDurationMinutes * 60 * 1000));
        
        if (slotEnd <= dayEnd) {
          timeSlots.push({
            tournament_id: this.tournamentId,
            start_time: slotStart.toISOString(),
            end_time: slotEnd.toISOString(),
            duration_minutes: matchDurationMinutes
          });
        }
        
        slotStart = new Date(slotEnd.getTime() + (breakMinutes * 60 * 1000));
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    const { error } = await supabase
      .from('tournament_time_slots')
      .insert(timeSlots);
    
    if (error) throw error;
    
    await this.loadTimeSlots();
  }
}
